import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, TextField, Typography, Button, MenuItem } from '@mui/material';
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dayjs } from 'dayjs';
import TextEditor from '../../components/TextEditor';
import CategorySelect from '../../components/post/CategorySelect';
import LocationSelect from '../../components/post/LocationSelect';
import VisaTypeSelect from '../../components/post/VisaTypeSelect';
import CustomDatePicker from '../../components/CustomDatePicker';
import { CreatePostData, CreatePostForm } from '../../types/types';
import { formatDate } from '../../hooks/formatDate';
import { usePostRequest } from '../../hooks/postRequest';
import { useNavigate } from 'react-router-dom';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { ADMIN_BASE_URL } from '../../utils/config';

const validationSchema = yup.object().shape({
  title: yup.string().min(5, "Title must be at least 5 characters").required("Title is required"),
  category: yup.object({
    id: yup.number().required("Category is required"),
    slug: yup.string().required("Category is required"),
    name: yup.string().required("Category is required"),
  }).required("Category is required"),
  location: yup.object({
    id: yup.number().required("Location is required"),
    slug: yup.string().required("Location is required"),
    name: yup.string().required("Location is required"),
  }).required("Location is required"),
  visaType: yup.object({
    id: yup.number().required("Visa type is required"),
    slug: yup.string().required("Visa type is required"),
    name: yup.string().required("Visa type is required"),
  }).required("Visa type is required"),
  salary: yup.number().typeError("Salary must be a number").required("Salary is required"),
  googleFormLink: yup.string().url("Invalid URL format").required("Form link is required"),
  japaneseLanguageLevel: yup.string().required("Japanese level is required"),
  infoOfNumOfWorkers: yup.string().required("Number of workers is required"),
  deadLine: yup.date().required("Deadline is required"),
  gender: yup.string().oneOf(["male", "female", "all"]).required("Gender is required"),
});

const CreateNewPost: React.FC = () => {
  const navigate = useNavigate();
  const postRequest = usePostRequest();
  const methods = useForm<CreatePostForm>({
    resolver: yupResolver(validationSchema)
  });
  const { handleSubmit, setValue, control, formState: { errors } } = methods;
  const [deadlineDate, setDeadlineDate] = useState<Dayjs | null>(null);
  const [description, setDescription] = useState<String | null>(null);

  const handleGoBack = () => {
    navigate('/admin/posts');
  }

  const onSubmit = async (data: CreatePostForm) => {
    if (description) {
      const formattedData: CreatePostData = {
        title: data.title,
        japaneseLanguageLevel: data.japaneseLanguageLevel,
        categoryId: data.category.id,
        visaTypeId: data.visaType.id,
        locationId: data.location.id,
        description: description,
        salary: data.salary,
        deadLine: formatDate(data.deadLine),
        gender: data.gender,
        googleFormLink: data.googleFormLink,
        infoOfNumOfWorkers: data.infoOfNumOfWorkers
      }
      const dispatchData = await postRequest({ baseUrl: ADMIN_BASE_URL, method: 'POST', data: formattedData, id: null, perPage: 12 });
      if (dispatchData.meta.requestStatus === 'fulfilled') {
        navigate('/admin/posts');
      }
    } else {
      console.log("You can't save the data");
    }
  };

  useEffect(() => {
    if (deadlineDate) {
      setValue('deadLine', deadlineDate.toDate());
    } else {
      setValue('deadLine', null as unknown as Date);
    }
  }, [deadlineDate, setValue]);

  return (
    <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: '8px' }}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete='off' style={{ margin: '0 auto' }}>
              <Box sx={{ display: 'flex', mb: 3 ,alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='h5'>Create New Post</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Button onClick={handleGoBack} sx={{ ml: 1, mr: 3 }} type='button'>Cancel</Button>
                  <Button startIcon={<LibraryAddIcon />} type="submit" variant="contained" color="primary">
                    Save
                  </Button>
                </Box>
              </Box>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    autoFocus
                    label="Title"
                    fullWidth
                    type='text'
                    variant='standard'
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.title}
                    helperText={errors.title ? errors.title.message : ''}
                  />
                )}
              />
              <Box sx={{ mx: 1, display: 'inline-block', width: 350, mb: 3 }}>
                <CategorySelect error={errors.category?.message || errors.category?.name?.message} />
              </Box>
              <Box sx={{ mx: 1, display: 'inline-block', width: 350, mb: 3 }}>
                <VisaTypeSelect error={errors.visaType?.message || errors.visaType?.name?.message} />
              </Box>
              <Box sx={{ mx: 1, display: 'inline-block', width: 350, mb: 3 }}>
                <LocationSelect error={errors.location?.message || errors.location?.name?.message} />
              </Box>
              <Controller
                name="japaneseLanguageLevel"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    label="Japanese Level"
                    fullWidth
                    type='text'
                    variant='standard'
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.japaneseLanguageLevel}
                    helperText={errors.japaneseLanguageLevel ? errors.japaneseLanguageLevel.message : ''}
                  />
                )}
              />
              <Controller
                name="salary"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    label="Salary"
                    type='text'
                    variant='standard'
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.salary}
                    helperText={errors.salary ? errors.salary.message : ''}
                  />
                )}
              />
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    label="Gender"
                    select
                    variant="standard"
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.gender}
                    helperText={errors.gender ? errors.gender.message : ''}
                    inputProps={{ 'aria-label': 'controlled' }}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="infoOfNumOfWorkers"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    autoFocus
                    label="Number Of Workers"
                    fullWidth
                    type='text'
                    variant='standard'
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.infoOfNumOfWorkers}
                    helperText={errors.infoOfNumOfWorkers ? errors.infoOfNumOfWorkers.message : ''}
                  />
                )}
              />
              <Controller
                name="googleFormLink"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    label="Form Link"
                    type='text'
                    variant='standard'
                    sx={{ mx: 1, width: 350, mb: 3 }}
                    error={!!errors.googleFormLink}
                    helperText={errors.googleFormLink ? errors.googleFormLink.message : ''}
                  />
                )}
              />
              <Box sx={{ mx: 1, display: 'inline-block', width: 350, mb: 3 }}>
                <Controller
                  name="deadLine"
                  control={control}
                  render={({ field }) => (
                    <CustomDatePicker
                      label={"Deadline"}
                      date={deadlineDate}
                      setDate={setDeadlineDate}
                      error={errors.deadLine?.message}
                      sx={{ width: '100%' }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ mx: 1, mb: 3, display: 'block', maxWidth: 700 }}>
                <TextEditor setDescription={setDescription} />
              </Box>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CreateNewPost;
